import {ConfEnv} from "@/config/config";
import {useThemeStore} from "@/store/useTheme.store";
import gamanzaDefaultAvatar from "@/assets/images/avatars/person_2.png";
import {buildGamanzaTranslations} from "./gamanzaTranslations";

/** Brand accent — Electric Teal `--accent-brand` (light + dark) */
const GAMANZA_PRIMARY = "#00CED1";
/** Dark widget surface — `--surface-1` */
const GAMANZA_DARK_BG = "#161A21";
/** Match site text tokens so points cards stay readable after theme switch */
const GAMANZA_TEXT_DARK = "#f9fafb";
const GAMANZA_TEXT_DARK_MUTED = "#e5e7eb";
const GAMANZA_TEXT_LIGHT = "#111827";
const GAMANZA_TEXT_LIGHT_MUTED = "#4b5563";
const GAMANZA_LOCALE = "es";
/** Bump to force destroy+re-init when translation/lookAndFeel/assets set changes */
const GAMANZA_TRANSLATIONS_REV = 5;

let gamanzaScriptLoadPromise = null;
let initializedToken = null;
let lastWidgets = null;
let lastAppliedTheme = null;
let lastAppliedLocale = null;
let lastAppliedTranslationsRev = null;
let engageInFlight = null;

export const buildGamanzaLookAndFeel = (theme = "light") => {
  const isDark = theme === "dark";
  return {
    typography: {
      fontFamily: '"Poppins", Roboto, Helvetica, Arial, sans-serif',
    },
    palette: {
      mode: isDark ? "dark" : "light",
      primary: {
        main: GAMANZA_PRIMARY,
      },
      text: {
        primary: isDark ? GAMANZA_TEXT_DARK : GAMANZA_TEXT_LIGHT,
        secondary: isDark ? GAMANZA_TEXT_DARK_MUTED : GAMANZA_TEXT_LIGHT_MUTED,
      },
      ...(isDark
        ? {
            background: {
              default: GAMANZA_DARK_BG,
              paper: GAMANZA_DARK_BG,
            },
          }
        : {}),
    },
  };
};

const getAppTheme = () => {
  const theme = useThemeStore.getState().theme;
  return theme === "dark" ? "dark" : "light";
};

export const ensureGamanzaWidgetsBundleLoaded = async () => {
  const scriptUrl = ConfEnv.GAMANZA.WIDGETS_SCRIPT_URL;
  if (!scriptUrl) {
    throw new Error("Gamanza widgets script URL is not configured (VITE_GAMANZA_WIDGETS_SCRIPT_URL).");
  }

  // Align with index.html: hint mobile UA before the widgets bundle evaluates isMobile.
  try {
    if (
      typeof navigator !== "undefined" &&
      window.matchMedia("(max-width: 1024px)").matches &&
      !/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
    ) {
      const ua =
        "Mozilla/5.0 (iPhone; CPU iPhone OS 17_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.4 Mobile/15E148 Safari/604.1";
      Object.defineProperty(navigator, "userAgent", {
        get: () => ua,
        configurable: true,
      });
      Object.defineProperty(navigator, "maxTouchPoints", {
        get: () => 5,
        configurable: true,
      });
    }
  } catch (_e) {}

  if (typeof window !== "undefined" && window.GamanzaEngageClient) {
    return;
  }

  if (gamanzaScriptLoadPromise) {
    return gamanzaScriptLoadPromise;
  }

  gamanzaScriptLoadPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);
    if (existingScript && window.GamanzaEngageClient) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = scriptUrl;
    script.async = true;

    script.onload = () => {
      if (window.GamanzaEngageClient) {
        resolve();
      } else {
        reject(new Error("GamanzaEngageClient not available after widgets script load"));
      }
    };

    script.onerror = () => reject(new Error("Failed to load Gamanza widgets script"));

    document.head.appendChild(script);
  });

  return gamanzaScriptLoadPromise;
};

/**
 * destroyWidgets is async via getInstance — must await before a new init,
 * otherwise Gamanza keeps the previous lookAndFeel singleton.
 * init() skips config() if window.GamificationWidgets already exists, so clear it.
 */
export const destroyGamanzaWidgets = () =>
  new Promise(resolve => {
    const finish = () => {
      try {
        if (typeof window !== "undefined") {
          window.GamificationWidgets = undefined;
        }
      } catch (_e) {}
      initializedToken = null;
      lastWidgets = null;
      lastAppliedTheme = null;
      lastAppliedLocale = null;
      lastAppliedTranslationsRev = null;
      resolve();
    };

    try {
      const client = window.GamanzaEngageClient;
      if (!client?.getInstance) {
        finish();
        return;
      }

      client.getInstance(widgets => {
        try {
          if (widgets && typeof widgets.destroyWidgets === "function") {
            widgets.destroyWidgets();
          } else if (widgets && typeof widgets.destroy === "function") {
            widgets.destroy();
          }
        } catch (_e) {}
        finish();
      });
    } catch (_e) {
      finish();
    }
  });

export const reloadGamanzaWidgets = () => {
  try {
    if (typeof lastWidgets?.reload === "function") {
      lastWidgets.reload(GAMANZA_LOCALE);
    }
  } catch (_e) {}
};

/** Mount/refresh a single SPA widget node by element id (Gamanza reloadOne). */
export const reloadGamanzaWidgetById = widgetId => {
  try {
    if (typeof lastWidgets?.reloadOne === "function") {
      lastWidgets.reloadOne(widgetId, GAMANZA_LOCALE);
      return;
    }
    reloadGamanzaWidgets();
  } catch (_e) {
    reloadGamanzaWidgets();
  }
};

/**
 * Ensure SDK is ready, then re-scan widgets after the next paint
 * (needed when SPA routes mount new .gamification_widget nodes).
 */
export const refreshGamanzaWidgetsAfterPaint = (widgetId = null) => {
  const run = () => {
    if (widgetId) reloadGamanzaWidgetById(widgetId);
    else reloadGamanzaWidgets();
  };
  requestAnimationFrame(() => {
    requestAnimationFrame(run);
  });
};

const initGamanzaEngage = (token, theme) =>
  new Promise((resolve, reject) => {
    try {
      window.GamanzaEngageClient.init(
        {
          init: {
            clientId: ConfEnv.GAMANZA.CLIENT_ID,
            playerIdentityToken: token,
            serviceUrl: ConfEnv.GAMANZA.SERVICE_URL,
            playerLocale: GAMANZA_LOCALE,
          },
          lookAndFeel: buildGamanzaLookAndFeel(theme),
          translations: buildGamanzaTranslations(),
          // Fallback when player has no rank image
          // @see https://docs.gamanzaengage.com/docs/frontend-integration/widgets-configuration
          assets: {
            avatar_image: gamanzaDefaultAvatar,
          },
        },
        gamificationWidgets => {
          initializedToken = token;
          lastWidgets = gamificationWidgets;
          lastAppliedTheme = theme;
          lastAppliedLocale = GAMANZA_LOCALE;
          lastAppliedTranslationsRev = GAMANZA_TRANSLATIONS_REV;
          reloadGamanzaWidgets();
          resolve(gamificationWidgets);
        }
      );
    } catch (err) {
      reject(err);
    }
  });

export const ensureGamanzaEngage = async token => {
  if (!token) {
    throw new Error("Gamanza identity token is required");
  }
  if (!ConfEnv.GAMANZA.CLIENT_ID || !ConfEnv.GAMANZA.SERVICE_URL) {
    throw new Error("Gamanza config incomplete");
  }

  await ensureGamanzaWidgetsBundleLoaded();

  const theme = getAppTheme();

  const isReady = () =>
    initializedToken === token &&
    lastWidgets &&
    lastAppliedTheme === theme &&
    lastAppliedLocale === GAMANZA_LOCALE &&
    lastAppliedTranslationsRev === GAMANZA_TRANSLATIONS_REV;

  if (isReady()) {
    reloadGamanzaWidgets();
    return lastWidgets;
  }

  if (engageInFlight) {
    await engageInFlight.catch(() => {});
    if (isReady()) {
      reloadGamanzaWidgets();
      return lastWidgets;
    }
  }

  engageInFlight = (async () => {
    // reload() cannot change lookAndFeel/translations — destroy then re-init
    await destroyGamanzaWidgets();
    return initGamanzaEngage(token, theme);
  })();

  try {
    return await engageInFlight;
  } finally {
    engageInFlight = null;
  }
};
